import { useEffect, useMemo, useState } from "react";

/**
 * Client-side pagination for bare-array datasets.
 *
 * Use when the API returns the FULL result set in one call (most CI RPCs) and
 * we want to slice on the frontend to avoid rendering hundreds of rows at once.
 * Pairs with the shared <Pagination> control:
 *
 *   const { page, setPage, pageItems, totalPages, totalItems } =
 *     useClientPagination(rows, { pageSize: 30 });
 *   ...
 *   <Body rows={pageItems} />
 *   <Pagination page={page} totalPages={totalPages} onPageChange={setPage}
 *               totalItems={totalItems} perPage={30} />
 *
 * Resets to page 1 whenever the input array reference changes (a new fetch
 * completes, or a filter narrows the set) so the user isn't stranded on a
 * now-out-of-range page.
 */
const useClientPagination = (rows, { pageSize = 30 } = {}) => {
  const items = Array.isArray(rows) ? rows : [];
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [rows]);

  const boundedPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => items.slice((boundedPage - 1) * pageSize, boundedPage * pageSize),
    [items, boundedPage, pageSize]
  );

  return { page: boundedPage, setPage, pageItems, totalPages, totalItems };
};

export default useClientPagination;
